import { createSlice } from '@reduxjs/toolkit';
import * as actions from './actions';
import { SeatingChartsState } from '../types';

const initialState: SeatingChartsState = {
  chart: null,
  charts: [],
  categories: [],
  templates: [],
  templateCharts: [],
  loading: false,
  error: null,
  designerToken: null,
};

const seatingChartsSlice = createSlice({
  name: 'seatingCharts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearChart: (state) => {
      state.chart = null;
      state.categories = [];
      state.designerToken = null;
    },
  },
  extraReducers: (builder) => {
    // Create seating chart
    builder.addCase(actions.createSeatingChart.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(actions.createSeatingChart.fulfilled, (state, action) => {
      state.loading = false;
      state.chart = action.payload;
    });
    builder.addCase(actions.createSeatingChart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to create seating chart';
    });

    // Fetch site seating charts
    builder.addCase(actions.fetchSiteSeatingCharts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(actions.fetchSiteSeatingCharts.fulfilled, (state, action) => {
      state.loading = false;
      state.charts = action.payload;
    });
    builder.addCase(actions.fetchSiteSeatingCharts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch seating charts';
    });

    // Fetch event seating chart
    builder.addCase(actions.fetchEventSeatingChart.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(actions.fetchEventSeatingChart.fulfilled, (state, action) => {
      state.loading = false;
      state.chart = action.payload;
    });
    builder.addCase(actions.fetchEventSeatingChart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch seating chart';
    });

    // Update seating chart
    builder.addCase(actions.updateSeatingChart.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.updateSeatingChart.fulfilled, (state, action) => {
      state.loading = false;
      state.chart = action.payload;
    });
    builder.addCase(actions.updateSeatingChart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to update seating chart';
    });

    // Delete seating chart
    builder.addCase(actions.deleteSeatingChart.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.deleteSeatingChart.fulfilled, (state) => {
      state.loading = false;
      state.chart = null;
      state.categories = [];
      state.designerToken = null;
    });
    builder.addCase(actions.deleteSeatingChart.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to delete seating chart';
    });

    // Update ticket assignments
    builder.addCase(actions.updateTicketAssignments.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.updateTicketAssignments.fulfilled, (state, action) => {
      state.loading = false;
      state.chart = action.payload.chart;
    });
    builder.addCase(actions.updateTicketAssignments.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to update ticket assignments';
    });

    // Fetch chart categories
    builder.addCase(actions.fetchChartCategories.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.fetchChartCategories.fulfilled, (state, action) => {
      state.loading = false;
      state.categories = action.payload;
    });
    builder.addCase(actions.fetchChartCategories.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch categories';
    });

    // Fetch designer token
    builder.addCase(actions.fetchDesignerToken.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.fetchDesignerToken.fulfilled, (state, action) => {
      state.loading = false;
      state.designerToken = action.payload.token;
    });
    builder.addCase(actions.fetchDesignerToken.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch designer token';
    });

    // Fetch templates
    builder.addCase(actions.fetchTemplates.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.fetchTemplates.fulfilled, (state, action) => {
      state.loading = false;
      state.templates = action.payload;
    });
    builder.addCase(actions.fetchTemplates.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch templates';
    });

    // Save as template
    builder.addCase(actions.saveAsTemplate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.saveAsTemplate.fulfilled, (state, action) => {
      state.loading = false;
      state.chart = action.payload;
    });
    builder.addCase(actions.saveAsTemplate.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to save template';
    });

    // Fetch template charts from template workspace
    builder.addCase(actions.fetchTemplateCharts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(actions.fetchTemplateCharts.fulfilled, (state, action) => {
      state.loading = false;
      state.templateCharts = action.payload;
    });
    builder.addCase(actions.fetchTemplateCharts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch template charts';
    });
  },
});

export const { clearError, clearChart } = seatingChartsSlice.actions;
export default seatingChartsSlice.reducer;
